<template>
  <div class="row">
    <div class="col-12 col-md-12 col-sm-6">
      <div v-if="label" :class="[`text-${labelPosition}`]">
        <label class="input-label">{{ label }}</label>
      </div>

      <label
        v-if="!canDownload && !disabled"
        class="form-control position-relative text-center mb-0 file-input-container"
        :class="{ 'was-Invalidated': error, 'input-disabled': disabled }"
      >
        <div class="file-select">
          <em class="fas fa-file-plus pr-2 text-green" />
          <span>Presione aquí para anejar documento</span>

          <input
            :id="id"
            class="file-select-input"
            ref="fileuploadRef"
            type="file"
            :accept="accept"
            @change="handleFileChange"
          />
        </div>
      </label>

      <div
        v-if="canDownload && !isDigitalSignature"
        class="d-flex align-items-center w-100 justify-content-start"
      >
        <div class="font-weight-normal">
          <label class="circle-green" />
          <label :id="`${id}-label`" class="mb-0">{{ fileLabel }}</label>
        </div>
        <div
          class="d-flex flex-row-reverse ml-2"
          :class="{ 'button-container': hasShortStyle }"
        >
          <button
            :id="`${id}-btn-remove-on-replace`"
            type="button"
            class="btn outline-danger"
            :class="{ 'input-disabled': disabled }"
            :disabled="disabled"
            :hidden="!isReplacing"
            @click="removeFile"
          >
            <i class="fas fa-times-circle" />
            <span v-if="!hasShortStyle" class="ml-1">Eliminar</span>
          </button>

          <button
            v-if="canReplace && attachmentId"
            :id="`${id}-btnReplace`"
            type="button"
            class="btn outline-success mr-2 ml-2"
            @click="replaceFile"
          >
            <i class="fas fa-repeat" />
            <span v-if="!hasShortStyle" class="ml-1">Reemplazar</span>
          </button>

          <input
            :id="`fileReplace-${id}`"
            type="file"
            :accept="accept"
            ref="fileReplaceRef"
            style="display: none;"
            @change="handleReplace"
          />

          <button
            v-if="canDelete && !isReplacing"
            type="button"
            class="btn outline-danger mr-2"
            @click="$emit('delete', attachmentId)"
          >
            <i class="fas fa-times-circle" />
            <span v-if="!hasShortStyle" class="ml-1">Eliminar</span>
          </button>

          <button
            v-if="canDownload"
            :id="`${id}-btnDownload`"
            type="button"
            class="btn outline-success mr-2 ml-2"
            @click="downloadFile"
          >
            <i class="fas fa-file-download" />
            <span v-if="!hasShortStyle" class="ml-1">Descargar</span>
          </button>
        </div>
      </div>

      <div
        v-if="canDownload && isDigitalSignature"
        class="d-flex align-items-center w-100 justify-content-start"
      >
        <div class="font-weight-normal">
          <label class="circle-green" />
          <label :id="`${fileLabel}-label`" class="mb-0">{{ name }}</label>
        </div>
        <div
          class="d-flex flex-row-reverse ml-2"
          :class="{ 'button-container': hasShortStyle }"
        >
          <button
            :id="`${id}-btnDownload`"
            type="button"
            class="btn outline-success mr-2 ml-2"
            @click="downloadDocumentDigitalSignature"
          >
            <i class="fas fa-file-download" />
            <span v-if="!hasShortStyle" class="ml-1">Descargar</span>
          </button>
        </div>
      </div>
      <Error v-if="error" variant="no-border" :message="customErrorMsg" />

      <div v-if="isLoadingFile && !disabled" class="progress mt-2">
        <div
          class="progress-bar progress-bar-striped bg-success text-center"
          role="progressbar"
          :style="{ width: currentLoad }"
          aria-valuenow="25"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          {{ this.progressLabel }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import peiServices from '@/services/pei/general';
import fileInputStatus from '@/utils/constants/fileInputStatus';
import Error from '@/components/common/Error';
import alertMixin from '@/mixin/alert.mixin';

export default {
  name: 'CustomFileInput',
  mixins: [alertMixin],
  meta: {
    description: 'Componente personalizado de entrada de archivos',
    slots: {
      default: {
        type: 'component',
        valid: [],
      },
    },
  },
  components: {
    Error,
  },
  props: {
    id: {
      type: String,
      default: 'fuFileUpload',
      meta: {
        description: 'Id del Componente CustomFileInput',
        example: 'IdCustomFileInput',
      },
    },
    label: {
      type: String,
      default: '',
      meta: {
        description: 'Control para mostrar texto',
        example: 'Texto requerido',
      },
    },
    labelPosition: {
      type: String,
      default: 'center',
      meta: {
        description: 'Control para posicionar texto',
        example: 'Texto requerido',
      },
    },
    placeholder: {
      type: String,
      default: '',
      meta: {
        description: 'Texto de referencia para el CustomFileInput',
        example: 'Texto requerido',
      },
    },
    value: {
      type: File,
      default: null,
      meta: {
        description: 'Parámetro de entrada del CustomFileInput',
        example: '',
      },
    },
    attachmentStatus: {
      type: String,
      default: fileInputStatus.Empty,
      meta: {
        description: 'Estado del archivo adjunto',
        example: 'Texto requerido',
      },
    },
    accept: {
      type: String,
      default: '*',
      meta: {
        description: 'Control para aceptar archivos',
        example: 'Texto requerido',
      },
    },
    multiple: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Estable selección múltiple',
        example: 'true',
      },
    },
    isDigitalSignature: {
      type: Boolean,
      default: false,
      meta: {
        description:
          'Define si se debe descargar documento desde portal de firma digital',
        example: 'true',
      },
    },
    canDelete: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Define si se debe puede mostrar boton de eliminar',
        example: 'true',
      },
    },
    canReplace: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Define si se debe mostrar boton de reemplazar',
        example: 'true',
      },
    },
    isReplacing: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Define si se debe mostrar boton de reemplazar',
        example: 'true',
      },
    },
    required: {
      type: String,
      default: function() {
        return '';
      },
      meta: {
        description: 'Estado requerido',
        example: 'Texto requerido',
      },
    },
    error: {
      type: String,
      default: '',
      meta: {
        description: 'Estado Error',
        example: 'Texto requerido',
      },
    },
    customErrorMsg: {
      type: String,
      default: function() {
        return this.error;
      },
      meta: {
        description: 'Mensaje de error encontrado',
        example: 'Texto requerido',
      },
    },
    hasCustomDownload: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Estado disabled',
        example: 'true',
      },
    },
    displayStyle: {
      type: String,
      default: 'short',
      meta: {
        description: 'Estable estilo de visualización',
        example: 'Texto requerido',
      },
    },
    attachmentId: {
      type: String,
      default: null,
      meta: {
        description: 'Establece ID de archivo adjunto',
        example: 'Texto requerido',
      },
    },
    isEdit: {
      type: Boolean,
      default: false,
      meta: {
        description: 'Posibilita editar',
        example: 'true',
      },
    },
    name: {
      type: String,
      default: '',
      meta: {
        description: '	Estable el nombre en el archivo',
        example: 'name',
      },
    },
    maxSize: {
      //Size in MB
      type: Number,
      default: 10,
      meta: {
        description: 'Establece un tamaño máximo',
        example: '',
      },
    },
    notCloudDownload: {
      type: Boolean,
      default: false,
      meta: {
        description:
          'La descarga del archivo no viene de la nube sino el mismo archivo seleccionado al input element',
        example: '',
      },
    },
  },
  methods: {
    validateFile(file) {
      //check extension and size
      const filename = file.name;
      const fileExtension = filename
        .split('.')
        [filename.split('.').length - 1].toLowerCase();
      const fileSize = (file.size / 1048576).toFixed(2);
      const acceptFileExtension = this.accept
        .split('.')
        [this.accept.split('.').length - 1].toLowerCase();
      let validationResult = true;

      //validate extension
      if (!fileExtension.includes(acceptFileExtension)) {
        this.ShowToast(
          'Error',
          `Favor de seleccionar un archivo de tipo ${acceptFileExtension.toUpperCase()}.`,
          'error'
        );
        validationResult = false;
      }

      //validate file size in mb
      if (fileSize > this.maxSize) {
        this.ShowToast(
          'Error',
          `Favor de seleccionar el archivo de tamaño menor o igual a ${this.maxSize}MB.`,
          'error'
        );
        validationResult = false;
      }

      return validationResult;
    },
    handleFileChange: function(e) {
      if (e.target.files && e.target.files.length > 0) {
        //validate file
        if (!this.validateFile(e.target.files[0])) {
          this.$refs.fileuploadRef.value = null;
          return;
        }

        this.$emit('input', e.target.files[0]);
        this.loadFile();
      }
    },
    downloadFile() {
      if (this.canDownload) {
        if (this.attachmentId && !this.isDeleting) {
          if (this.hasCustomDownload) {
            this.$emit('download');
            return;
          }
          
          peiServices.downloadDocumentByAttachmentId(this.attachmentId);
        } else {
          let a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          let url = this.fileReader.result;
          a.href = url;
          a.download = this.file.name;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    },
    downloadDocumentDigitalSignature() {
      if (this.canDownload) {
        if (this.attachmentId && !this.isDeleting) {
          peiServices.getSignatureDocument(this.attachmentId, true);
        } else {
          let a = document.createElement('a');
          document.body.appendChild(a);
          a.style = 'display: none';
          let url = this.fileReader.result;
          a.href = url;
          a.download = this.file.name;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    },
    replaceFile() {
      this.$refs.fileReplaceRef.click();
    },
    handleReplace(e) {
      if (!e.target.files || e.target.files.length <= 0) return;

      if (!this.validateFile(e.target.files[0])) {
        this.$refs.fileReplaceRef.value = null;
        return;
      }

      this.$emit('replace', e.target.files[0]);

      const fileInput = document.getElementById(`fileReplace-${this.id}`);
      fileInput.value = '';
    },
    removeFile: function(e) {
      e.preventDefault();
      this.$emit('input', null);
      this.currentLoad = '0%';
      this.status = fileInputStatus.Deleted;
      this.fileReader = null;
      this.file = null;
      this.fileLabel = null;
      this.isDeleting = true;
    },
    onLoadProgress(event) {
      let loaded = (event.loaded / event.total) * 100;
      this.status = fileInputStatus.Loading;
      this.currentLoad = loaded + '%';
    },
    onLoad() {
      this.status = fileInputStatus.Loaded;
    },
    loadFile() {
      let file = document.querySelector(`#${this.id}`).files[0];
      let reader = new FileReader();

      reader.onprogress = this.onLoadProgress;
      reader.onloadend = this.onLoad;
      reader.addEventListener('onprogress', this.onLoadProgress);

      if (file) {
        reader.readAsDataURL(file);
        this.fileReader = reader;
        this.file = file;
        this.fileLabel = file.name;
      }
    },
  },
  data() {
    return {
      currentLoad: '0%',
      status: this.attachmentStatus,
      fileReader: null,
      file: null,
      fileLabel: this.name,
      isDeleting: false,
      fileMaxSize: this.maxSize,
    };
  },
  computed: {
    hasShortStyle() {
      return this.displayStyle === 'normal';
    },
    isLoadingFile() {
      return this.status === fileInputStatus.Loading;
    },
    canDownload() {
      return (
        (this.attachmentId != null && this.status != fileInputStatus.Deleted) ||
        (this.status === fileInputStatus.Loaded &&
          this.fileReader !== null &&
          this.fileReader.result != null) ||
        this.isEdit ||
        (this.notCloudDownload && this.status === fileInputStatus.Loaded)
      );
    },
    progressLabel() {
      return this.status !== fileInputStatus.Loaded ? 'Cargando' : 'Completado';
    },
  },
};
</script>

<style scoped>
.file-select {
  position: relative;
  display: block;
  text-align: center;
  margin-bottom: 0px !important;
}

.file-select-input {
  display: block !important;
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
button {
  color: #3f5b60;
  background-color: #ffffff;
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  text-align: center;
  border-radius: 4px;
  height: 2.375rem;
  border: 1px solid #3f5b60;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  cursor: pointer;
  padding: 5px;
  padding-left: 10px;
  padding-right: 10px;
  white-space: nowrap;
}

input[type='file'] {
  display: none;
}

.success {
  color: #ffffff;
  background-color: #2db782;
  border-color: #2db782;
}

.outline-success {
  color: #2db782;
  background-color: #ffffff;
  border-color: #2db782;
}

.danger {
  color: #ffffff;
  background-color: #c9545e;
  border-color: #c9545e;
}

.outline-danger {
  color: #c9545e;
  background-color: #ffffff;
  border-color: #c9545e;
}
.outline-danger:hover {
  background-color: #c9545e;
  color: #ffffff;
}
.outline-success:hover {
  background-color: #2db782;
  color: #ffffff;
}

.input-disabled {
  background-color: #e9ecef;
  cursor: initial;
}

.button-container button {
  height: 33px;
  width: 33px;
  padding: 0;
}

.file-input-container {
  height: auto !important;
}
</style>
